/**
 * VideoLinkManager — controlled input that lets a user add up to N
 * YouTube / Vimeo links to a form. Renders next to the "Upload
 * photos" caption on Sports pages (and, later, everywhere else).
 *
 * Props
 *   value:     string[]     — current list of raw URLs
 *   onChange:  (list) => void
 *   max:       number       — hard cap (default 8)
 *   label:     string       — top caption (default "Link videos")
 */
import { useState } from "react";
import { toast } from "sonner";
import { Video, Trash2, Plus, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import VideoEmbed from "./VideoEmbed";

export default function VideoLinkManager({
  value = [], onChange, max = 8, label = "Link videos",
  helper = "Paste a YouTube or Vimeo URL. Videos are embedded on the page."
}) {
  const [input, setInput] = useState("");
  const [busy,  setBusy]  = useState(false);

  const add = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const url = input.trim();
    if (!url) return;
    if ((value || []).length >= max) { toast.error(`You can link up to ${max} videos.`); return; }
    if ((value || []).includes(url)) { toast.info("That link is already added."); return; }
    setBusy(true);
    try {
      // Server does the normalisation; we only need it to reject junk early.
      await http.get("/video/resolve", { params: { url } });
      onChange?.([...(value || []), url]);
      setInput("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That link isn't a supported video URL.");
    } finally {
      setBusy(false);
    }
  };

  const remove = (i) => {
    const next = (value || []).slice();
    next.splice(i, 1);
    onChange?.(next);
  };

  return (
    <div className="space-y-2" data-testid="video-link-manager">
      <div className="flex items-center gap-2">
        <Video className="w-4 h-4 opacity-70" />
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-70">{label}</div>
        <span className="text-[10px] opacity-50">· {value?.length || 0}/{max}</span>
      </div>
      {helper && <p className="text-xs opacity-60">{helper}</p>}

      <div className="flex gap-2">
        <input
          type="url"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); add(e); } }}
          placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
          className="flex-1 px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="video-link-input"
        />
        <button
          type="button" onClick={add} disabled={busy || !input.trim()}
          className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40"
          data-testid="video-link-add-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add
        </button>
      </div>

      {(value || []).length > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2 mt-3">
          {value.map((url, i) => (
            <li key={i} className="wofy-card !p-2 relative" data-testid={`video-link-item-${i}`}>
              <VideoEmbed url={url} />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] font-mono opacity-60 truncate">{url}</span>
                <button
                  onClick={() => remove(i)} type="button"
                  className="text-xs text-red-600 inline-flex items-center gap-0.5 hover:underline"
                  data-testid={`video-link-remove-${i}`}
                >
                  <Trash2 className="w-3 h-3" />Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
